import { logoLoaderPhaseMs } from './logo-loader.handoff';
import {
  LOGO_EASE_DEFAULT,
  LOGO_GEOMETRY,
  LOGO_LOADER_COLORS,
  LOGO_LOADER_DELAY_MS,
  LOGO_LOADER_DOT_TRACKS,
  LOGO_LOADER_DOTS_DURATION_MS,
  LOGO_LOADER_DURATION_MS,
  LOGO_LOADER_FADE_IN_MS,
  LOGO_LOADER_REDUCED_DURATION_MS,
  LOGO_LOADER_REDUCED_TRACKS,
  LOGO_LOADER_TRACKS,
  type LogoEase,
  type LogoTrack,
} from './logo-loader.motion';

/**
 * Web renderer of the logo loader: CSS keyframes generated from the shared
 * tracks, plus the static SVG markup. Plain strings, so the SAME output serves
 * the React component and flashka-web/index.html (inlined at build time by a
 * Vite plugin, which is what lets the boot loader draw before any JS runs).
 *
 * Only `transform` and `opacity` animate. Colours come from `--logo-*`
 * custom properties, switched by the `.dark` class on <html>.
 */

const P = 'fl-logo-loader';

const ease = (e: LogoEase) => `cubic-bezier(${e.join(',')})`;
const pct = (at: number) => `${+(at * 100).toFixed(3)}%`;
const n = (v: number) => +v.toFixed(5);

function keyframes(
  name: string,
  track: LogoTrack,
  render: (v: readonly number[]) => string,
) {
  const frames = track.map((kf, i) => {
    const last = i === track.length - 1;
    const timing = last
      ? ''
      : ` animation-timing-function: ${ease(kf.ease ?? LOGO_EASE_DEFAULT)};`;
    return `${pct(kf.at)} { ${render(kf.values)};${timing} }`;
  });
  return `@keyframes ${name} { ${frames.join(' ')} }`;
}

const T = LOGO_LOADER_TRACKS;
const R = LOGO_LOADER_REDUCED_TRACKS;
const G = LOGO_GEOMETRY;

/** All the loader's CSS. Idempotent: injecting it twice changes nothing. */
export const LOGO_LOADER_CSS = [
  `.${P} { --logo-frame: ${LOGO_LOADER_COLORS.light.frame}; --logo-card-a: ${LOGO_LOADER_COLORS.light.cardA}; --logo-card-b: ${LOGO_LOADER_COLORS.light.cardB};` +
    ` position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center;` +
    ` opacity: 0; animation: ${P}-in ${LOGO_LOADER_FADE_IN_MS}ms ease-out ${LOGO_LOADER_DELAY_MS}ms forwards; }`,
  `.${P}[data-immediate] { opacity: 1; animation: none; }`,
  `.dark .${P} { --logo-frame: ${LOGO_LOADER_COLORS.dark.frame}; --logo-card-a: ${LOGO_LOADER_COLORS.dark.cardA}; --logo-card-b: ${LOGO_LOADER_COLORS.dark.cardB}; }`,
  `.${P}-fill { position: absolute; inset: 0; }`,
  // The deck squashes past its own box: never clip it.
  `.${P}-mark { display: block; overflow: visible; flex: none; }`,
  `.${P}-f { fill: var(--logo-frame); } .${P}-ca { fill: var(--logo-card-a); } .${P}-cb { fill: var(--logo-card-b); }`,
  // `--fl-ll-phase` (a negative delay) puts every loader on one page clock, so
  // when one loader hands over to the next the cards carry on mid-shuffle.
  `.${P}-mark .${P}-anim { transform-box: fill-box; transform-origin: 50% 50%; animation-duration: ${LOGO_LOADER_DURATION_MS}ms;` +
    ` animation-delay: var(--fl-ll-phase, 0ms); animation-iteration-count: infinite; animation-fill-mode: both; }`,
  // The deck pivots on the frame's bottom centre. view-box, not fill-box: the
  // group's fill-box includes the moving cards, so its origin would drift.
  `.${P}-mark .${P}-deck { transform-box: view-box; transform-origin: ${n(G.width / 2)}px ${G.height}px; animation-name: ${P}-deck; }`,
  `.${P}-mark .${P}-ay { animation-name: ${P}-ay; } .${P}-mark .${P}-ax { animation-name: ${P}-ax; } .${P}-mark .${P}-as { animation-name: ${P}-as; }`,
  `.${P}-mark .${P}-by { animation-name: ${P}-by; } .${P}-mark .${P}-bx { animation-name: ${P}-bx; } .${P}-mark .${P}-bs { animation-name: ${P}-bs; }`,
  // The label hangs BELOW the mark, out of flow: a loader with a label keeps its
  // mark exactly where a bare one has it, so a hand-over never jumps.
  `.${P}-label { position: absolute; top: calc(100% + 16px); left: 50%; transform: translateX(-50%);` +
    ` display: inline-flex; align-items: baseline; white-space: nowrap; font-family: "Mona Sans", system-ui, sans-serif; }`,
  // Fixed width: the label never shifts as the dots come and go.
  `.${P}-dots { display: inline-block; width: 1.1em; text-align: left; }`,
  `.${P}-dots i { font-style: normal; opacity: 0; animation: ${LOGO_LOADER_DOTS_DURATION_MS}ms infinite both; }`,
  `.${P}-dots i:nth-child(1) { animation-name: ${P}-dot1; } .${P}-dots i:nth-child(2) { animation-name: ${P}-dot2; } .${P}-dots i:nth-child(3) { animation-name: ${P}-dot3; }`,
  `@keyframes ${P}-in { to { opacity: 1; } }`,
  keyframes(`${P}-deck`, T.deck, (v) => `transform: scale(${n(v[0]!)},${n(v[1]!)})`),
  keyframes(`${P}-ay`, T.aY, (v) => `transform: translateY(${n(v[0]!)}px)`),
  keyframes(`${P}-ax`, T.aX, (v) => `transform: translateX(${n(v[0]!)}px)`),
  keyframes(`${P}-as`, T.aS, (v) => `transform: scale(${n(v[0]!)})`),
  keyframes(`${P}-by`, T.bY, (v) => `transform: translateY(${n(v[0]!)}px)`),
  keyframes(`${P}-bx`, T.bX, (v) => `transform: translateX(${n(v[0]!)}px)`),
  keyframes(`${P}-bs`, T.bS, (v) => `transform: scale(${n(v[0]!)})`),
  ...LOGO_LOADER_DOT_TRACKS.map((track, i) =>
    keyframes(`${P}-dot${i + 1}`, track, (v) => `opacity: ${n(v[0]!)}`),
  ),
  keyframes(`${P}-rm-a`, R.a, (v) => `opacity: ${n(v[0]!)}`),
  keyframes(`${P}-rm-b`, R.b, (v) => `opacity: ${n(v[0]!)}`),
  `@media (prefers-reduced-motion: reduce) {` +
    ` .${P}-mark .${P}-anim { animation-name: none; }` +
    ` .${P}-mark .${P}-as { animation-name: ${P}-rm-a; animation-duration: ${LOGO_LOADER_REDUCED_DURATION_MS}ms; animation-delay: var(--fl-ll-phase-rm, 0ms); }` +
    ` .${P}-mark .${P}-bs { animation-name: ${P}-rm-b; animation-duration: ${LOGO_LOADER_REDUCED_DURATION_MS}ms; animation-delay: var(--fl-ll-phase-rm, 0ms); }` +
    ` .${P}-dots i { animation: none; opacity: 1; } }`,
].join('\n');

/**
 * The mark's SVG markup. `heightPx` sizes it; the width follows the logo's
 * aspect ratio. Decorative: the wrapper carries the status semantics.
 */
export function logoLoaderMarkSvg(heightPx: number): string {
  const w = n((heightPx * G.width) / G.height);
  const c = G.card;
  const card = (cls: string, y: number, axis: 'a' | 'b') =>
    `<g class="${P}-anim ${P}-${axis}y"><g class="${P}-anim ${P}-${axis}x">` +
    `<rect class="${P}-anim ${P}-${axis}s ${cls}" x="${c.x}" y="${y}" width="${c.width}" height="${c.height}" rx="${c.radius}"/>` +
    `</g></g>`;
  return (
    `<svg class="${P}-mark" viewBox="0 0 ${G.width} ${G.height}" width="${w}" height="${heightPx}" aria-hidden="true" focusable="false">` +
    `<g class="${P}-anim ${P}-deck">` +
    `<rect class="${P}-f" width="${G.width}" height="${G.height}" rx="${G.frameRadius}"/>` +
    // Violet first: amber draws on top, so it passes in FRONT on the crossing.
    card(`${P}-cb`, G.cardBY, 'b') +
    card(`${P}-ca`, G.cardAY, 'a') +
    `</g></svg>`
  );
}

/** The CSS custom properties that put a loader on the shared page clock. */
export function logoLoaderPhaseVars(
  now?: number,
): Record<'--fl-ll-phase' | '--fl-ll-phase-rm', string> {
  return {
    '--fl-ll-phase': `-${logoLoaderPhaseMs(LOGO_LOADER_DURATION_MS, now)}ms`,
    '--fl-ll-phase-rm': `-${logoLoaderPhaseMs(LOGO_LOADER_REDUCED_DURATION_MS, now)}ms`,
  };
}

/** Id of the boot loader index.html draws before any script runs. */
export const LOGO_LOADER_BOOT_ID = 'fl-boot-loader';

/**
 * The pre-React boot loader for index.html: the stylesheet, the dotted ground,
 * the mark, and a one-line script that puts it on the page clock. It sits NEXT
 * TO #root (the SEO middleware needs an empty #root). The app retires it
 * (`releaseBootLogoLoader`) once #root draws something — on the landing by
 * flying the mark into the nav's own logo. The ground is its own layer so it
 * can fade while the mark travels. `aria-label` is the brand name: no
 * translation exists yet at this point.
 */
export function logoLoaderBootHtml(heightPx = 48): string {
  return (
    `<style id="${P}-css">${LOGO_LOADER_CSS}\n` +
    `#${LOGO_LOADER_BOOT_ID} { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; isolation: isolate; }\n` +
    // The app's dotted canvas (web DotBackground), from the same theme
    // variables, so the hand-over to the app's PageLoader keeps the ground.
    `#${LOGO_LOADER_BOOT_ID} .${P}-ground { position: absolute; inset: 0; z-index: -1; background-color: rgb(var(--background-rgb));` +
    ` background-size: 10px 10px; background-image: radial-gradient(rgb(var(--dot-rgb)) 1.5px, transparent 0px); }</style>` +
    // The ground is up at once; only the mark waits out its grace and fades in.
    `<div id="${LOGO_LOADER_BOOT_ID}"><div class="${P}-ground"></div>` +
    `<div class="${P}" role="status" aria-busy="true" aria-label="Flashka">` +
    logoLoaderMarkSvg(heightPx) +
    `</div></div>` +
    `<script>(function(){var e=document.getElementById('${LOGO_LOADER_BOOT_ID}'),t=performance.now();e.dataset.t=String(t);` +
    `e.style.setProperty('--fl-ll-phase','-'+Math.round(t%${LOGO_LOADER_DURATION_MS})+'ms');` +
    `e.style.setProperty('--fl-ll-phase-rm','-'+Math.round(t%${LOGO_LOADER_REDUCED_DURATION_MS})+'ms');})();</script>` +
    // Without JavaScript nothing will ever replace it: the crawler copy shows.
    `<noscript><style>#${LOGO_LOADER_BOOT_ID} { display: none; }</style></noscript>`
  );
}

/** Marks the visible logo a boot loader may fly into (see releaseBootLogoLoader). */
export const LOGO_LOADER_FLIGHT_TARGET_ATTR = 'data-fl-logo-target';

/** Class names the React renderer shares with the markup above. */
export const LOGO_LOADER_CLASS = {
  root: P,
  fill: `${P}-fill`,
  label: `${P}-label`,
  ground: `${P}-ground`,
  mark: `${P}-mark`,
  anim: `${P}-anim`,
  dots: `${P}-dots`,
} as const;
